import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Avatar,
  Button,
  IconButton,
  Textarea,
  Spinner,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useToast,
} from "@chakra-ui/react";
import {
  FiArrowLeft,
  FiArrowDown,
  FiSend,
  FiInfo,
  FiPlus,
  FiBookmark,
  FiCopy,
  FiEdit2,
  FiRefreshCw,
  FiFastForward,
  FiImage,
  FiX,
} from "react-icons/fi";
import { ColorModeSwitcher } from "./ColorModeSwitcher";
import { useSession } from "./session";
import useConversation from "./useConversation";
import MessageMarkdown from "./MessageMarkdown";

function SceneImage({ image, conversationId, onClick }) {
  const { api } = useSession();
  const [src, setSrc] = useState(image.url || ""),
    [failed, setFailed] = useState(false);
  useEffect(() => {
    if (image.url) {
      setSrc(image.url);
      return;
    }
    const abort = new AbortController();
    let objectUrl;
    api(`/api/conversations/${conversationId}/images/${image.id}`, {
      signal: abort.signal,
    })
      .then((r) => r.blob())
      .then((blob) => {
        if (abort.signal.aborted) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch((e) => {
        if (e.name !== "AbortError") setFailed(true);
      });
    return () => {
      abort.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [api, image.id, image.url, conversationId]);
  return (
    <button
      className="scene-preview"
      onClick={() => src && onClick(src)}
      disabled={!src}
      aria-label="View scene image"
    >
      {src ? <img src={src} alt="Generated story scene" /> : <FiImage />}
      <span>{failed ? "Scene unavailable" : "Scene illustration"}</span>
      <span className="text-link">View</span>
    </button>
  );
}
export default function ChatPage() {
  const chat = useConversation(),
    { auth, favorites, toggleFavorite, favoriteBusy } = useSession(),
    location = useLocation(),
    toast = useToast();
  const [draft, setDraft] = useState(""),
    [details, setDetails] = useState(false),
    [hideSide, setHideSide] = useState(false),
    [preview, setPreview] = useState(""),
    [edit, setEdit] = useState(null),
    [editText, setEditText] = useState(""),
    [atBottom, setAtBottom] = useState(true);
  const scroller = useRef(null),
    textarea = useRef(null),
    stick = useRef(true),
    scrollSnapshot = useRef(null),
    previousIdentity = useRef("");
  const c = chat.character,
    identity = c?.publicId;
  useEffect(() => {
    if (previousIdentity.current !== identity) {
      setDraft("");
      setEdit(null);
      setDetails(false);
      setPreview("");
      stick.current = true;
      previousIdentity.current = identity;
    }
  }, [identity]);
  useEffect(() => {
    const viewport = window.visualViewport;
    const resize = () => {
      document.documentElement.style.setProperty(
        "--chat-height",
        `${viewport?.height || window.innerHeight}px`,
      );
    };
    resize();
    viewport?.addEventListener("resize", resize);
    return () => {
      viewport?.removeEventListener("resize", resize);
      document.documentElement.style.removeProperty("--chat-height");
    };
  }, []);
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    if (scrollSnapshot.current) {
      el.scrollTop =
        el.scrollHeight -
        scrollSnapshot.current.height +
        scrollSnapshot.current.top;
      scrollSnapshot.current = null;
    } else if (stick.current) el.scrollTop = el.scrollHeight;
  }, [chat.messages, chat.pending, chat.images]);
  useEffect(() => {
    if (textarea.current) {
      textarea.current.style.height = "auto";
      textarea.current.style.height = `${Math.min(152, Math.max(68, textarea.current.scrollHeight))}px`;
    }
  }, [draft]);
  const send = async () => {
    const text = draft.trim();
    if (!text || chat.generating || chat.busy) return;
    stick.current = true;
    setAtBottom(true);
    setDraft("");
    await chat.run("send", text);
  };
  const last = chat.messages[chat.messages.length - 1],
    userIndex = chat.messages.map((m) => m.role).lastIndexOf("user");
  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({ title: "Copied", duration: 1500 });
    } catch {
      toast({ title: "Copy unavailable in this browser", status: "error" });
    }
  };
  const startEdit = (m) => {
    setEdit(m);
    setEditText(m.content);
  };
  const saveEdit = async () => {
    if (!editText.trim()) return;
    const ok =
      edit.role === "assistant"
        ? await chat.editAssistant(edit.id, editText)
        : await chat.run("edit", editText, edit.id);
    if (ok) setEdit(null);
  };
  const profile = (
    <div className="character-profile">
      {chat.unavailable && (
        <p>
          This character is no longer available. Your saved conversation is
          read-only.
        </p>
      )}
      {c && !chat.unavailable && (
        <>
          <img className="profile-portrait" src={c.image} alt={c.name} />
          <h2>{c.name}</h2>
          <p>{c.description}</p>
          <div className="character-tags">
            {c.tags?.slice(0, 4).map((t) => (
              <span key={t}>{t}</span>
            ))}
          </div>
          <p className="profile-author">@{c.author || "creator"}</p>
          <Button
            variant="outline"
            size="sm"
            leftIcon={
              <FiBookmark
                fill={favorites.has(c.publicId) ? "currentColor" : "none"}
              />
            }
            isLoading={favoriteBusy.has(c.publicId)}
            onClick={() => toggleFavorite(c.publicId)}
          >
            {favorites.has(c.publicId) ? "Saved" : "Save character"}
          </Button>
          {c.scenario && (
            <section>
              <h3>About this story</h3>
              <p>{c.scenario}</p>
            </section>
          )}
          {c.source && (
            <section>
              <h3>Character source</h3>
              {c.source.url ? (
                <a
                  className="text-link"
                  href={
                    /^https?:\/\//.test(c.source.url) ? c.source.url : undefined
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  {c.source.name}
                </a>
              ) : (
                <p>{c.source.name}</p>
              )}
            </section>
          )}
          {chat.images.length > 0 && (
            <section>
              <h3>Scene gallery</h3>
              {chat.images.map((img) => (
                <SceneImage
                  key={img.id}
                  image={img}
                  conversationId={chat.conversationId}
                  onClick={setPreview}
                />
              ))}
            </section>
          )}
        </>
      )}
    </div>
  );
  return (
    <div className={`chat-page${hideSide ? " side-hidden" : ""}`}>
      <section className="chat-column">
        <header className="chat-header">
          <IconButton
            as={Link}
            to={location.state?.from || "/"}
            aria-label="Back to discovery"
            icon={<FiArrowLeft />}
            variant="ghost"
          />
          <Avatar size="sm" src={c?.thumb} name={c?.name} />
          <div className="chat-title">
            <h1>{c?.name || "Your story"}</h1>
            <span>AI character{c?.tags?.[0] ? ` · ${c.tags[0]}` : ""}</span>
          </div>
          <div className="chat-header-actions">
            <ColorModeSwitcher />
            <IconButton
              aria-label="New conversation"
              icon={<FiPlus />}
              variant="ghost"
              isDisabled={chat.generating || chat.busy || chat.unavailable}
              onClick={chat.newChat}
            />
            <IconButton
              aria-label="Character details"
              icon={<FiInfo />}
              variant="ghost"
              onClick={() => {
                if (window.innerWidth >= 1280) setHideSide((v) => !v);
                else setDetails(true);
              }}
            />
          </div>
        </header>
        {chat.unavailable && (
          <div className="guest-notice" role="status">
            This character is no longer available. You can still read your saved
            conversation.
          </div>
        )}
        {!auth.isAuthenticated && (
          <div className="guest-notice">
            Temporary chat ·{" "}
            <button onClick={() => auth.login()} className="text-link">
              Log in to save your stories
            </button>
          </div>
        )}
        <div
          className="message-scroll"
          ref={scroller}
          onScroll={() => {
            const el = scroller.current;
            stick.current =
              el.scrollHeight - el.scrollTop - el.clientHeight < 100;
            setAtBottom(stick.current);
          }}
        >
          <div className="message-container">
            {chat.loading ? (
              <div className="empty-state">
                <Spinner />
                <p>Opening your story…</p>
              </div>
            ) : (
              <>
                {chat.hasMore && (
                  <div className="older-messages">
                    <Button
                      size="sm"
                      variant="outline"
                      isLoading={chat.loadingMore}
                      onClick={() => {
                        scrollSnapshot.current = {
                          height: scroller.current.scrollHeight,
                          top: scroller.current.scrollTop,
                        };
                        chat.more();
                      }}
                    >
                      Load earlier messages
                    </Button>
                  </div>
                )}
                {chat.messages.map((m, index) => (
                  <React.Fragment key={m.id}>
                    <article
                      className={`message ${m.role === "user" ? "user-message" : "assistant-message"}`}
                    >
                      <Avatar
                        size="sm"
                        name={m.role === "user" ? "You" : c?.name}
                        src={
                          m.role === "assistant" ? c?.thumb : auth.user?.picture
                        }
                      />
                      <div className="message-content">
                        <div className="message-heading">
                          <strong>{m.role === "user" ? "You" : c?.name}</strong>
                          {m.createdAt && (
                            <time>
                              {new Date(m.createdAt).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </time>
                          )}
                        </div>
                        <MessageMarkdown text={m.content} />
                        {m.status === "interrupted" && (
                          <span className="interrupted-label">
                            Incomplete reply
                          </span>
                        )}
                        <div className="message-actions">
                          <IconButton
                            size="xs"
                            aria-label="Copy message"
                            icon={<FiCopy />}
                            variant="ghost"
                            onClick={() => copy(m.content)}
                          />
                          {((index === chat.messages.length - 1 &&
                            m.role === "assistant") ||
                            (index === userIndex &&
                              index >= chat.messages.length - 2)) && (
                            <IconButton
                              size="xs"
                              aria-label="Edit message"
                              icon={<FiEdit2 />}
                              variant="ghost"
                              isDisabled={
                                chat.generating || chat.busy || chat.unavailable
                              }
                              onClick={() => startEdit(m)}
                            />
                          )}
                        </div>
                      </div>
                    </article>
                    {chat.images
                      .filter((img) => img.messageId === m.id)
                      .map((img) => (
                        <div className="message-scene" key={img.id}>
                          <SceneImage
                            image={img}
                            conversationId={chat.conversationId}
                            onClick={setPreview}
                          />
                        </div>
                      ))}
                  </React.Fragment>
                ))}
                {chat.generating && (
                  <article
                    className="message assistant-message"
                    aria-live="polite"
                  >
                    <Avatar size="sm" name={c?.name} src={c?.thumb} />
                    <div className="message-content">
                      <div className="message-heading">
                        <strong>{c?.name}</strong>
                        <span>Writing…</span>
                      </div>
                      {chat.pending ? (
                        <MessageMarkdown text={chat.pending} />
                      ) : (
                        <span
                          className="typing-dots"
                          aria-label="Generating reply"
                        >
                          •••
                        </span>
                      )}
                    </div>
                  </article>
                )}
                {!chat.generating &&
                  !chat.busy &&
                  !chat.unavailable &&
                  last?.role === "assistant" && (
                    <div className="reply-actions">
                      {userIndex >= 0 && (
                        <Button
                          size="sm"
                          variant="outline"
                          leftIcon={<FiRefreshCw />}
                          onClick={() => chat.run("regenerate")}
                        >
                          Regenerate
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        leftIcon={<FiFastForward />}
                        onClick={() => chat.run("continue")}
                      >
                        Continue
                      </Button>
                    </div>
                  )}
              </>
            )}
            {chat.sceneError && (
              <div className="chat-alert" role="status">
                {chat.sceneError}
              </div>
            )}
            {chat.busy && !chat.generating && (
              <div className="chat-alert" role="status">
                A reply is being generated on another device.{" "}
                <button className="text-link" onClick={chat.refresh}>
                  Refresh chat
                </button>
              </div>
            )}
            {chat.error && (
              <div className="chat-alert" role="alert">
                <p>{chat.error}</p>
                <div className="inline-actions">
                  {chat.retryInfo && (
                    <Button
                      size="sm"
                      onClick={chat.retry}
                      isDisabled={
                        chat.generating || chat.busy || chat.unavailable
                      }
                    >
                      Retry reply
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={chat.refresh}>
                    Refresh chat
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
        {!atBottom && (
          <Button
            className="jump-latest"
            size="sm"
            leftIcon={<FiArrowDown />}
            onClick={() => {
              stick.current = true;
              scroller.current.scrollTo({
                top: scroller.current.scrollHeight,
                behavior: "smooth",
              });
            }}
          >
            Back to latest
          </Button>
        )}
        <footer className="composer-wrap">
          <form
            className="composer"
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
          >
            <Textarea
              ref={textarea}
              aria-label="Message"
              placeholder="What happens next?"
              value={draft}
              maxLength={4000}
              rows={2}
              resize="none"
              isDisabled={!c || chat.loading || chat.busy || chat.unavailable}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  !e.shiftKey &&
                  !e.nativeEvent.isComposing &&
                  window.matchMedia("(pointer: fine)").matches &&
                  window.innerWidth >= 768
                ) {
                  e.preventDefault();
                  send();
                }
              }}
            />
            <IconButton
              type="submit"
              aria-label="Send message"
              variant="brand"
              icon={<FiSend />}
              isLoading={chat.generating}
              isDisabled={
                !draft.trim() ||
                !c ||
                chat.loading ||
                chat.busy ||
                chat.unavailable
              }
            />
          </form>
          <div className="composer-hint">
            <span className="keyboard-hint">
              Enter to send · Shift + Enter for a new line
            </span>
            <span>
              {chat.unavailable
                ? "Read-only conversation"
                : chat.generating
                  ? "Generating…"
                  : chat.conversationId
                    ? chat.error
                      ? "Sync needs attention"
                      : "Saved to your account"
                    : "Temporary chat"}
            </span>
          </div>
        </footer>
      </section>
      <aside className="profile-sidebar">{profile}</aside>
      <Drawer
        isOpen={details}
        onClose={() => setDetails(false)}
        placement="right"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Character details</DrawerHeader>
          <DrawerBody>{profile}</DrawerBody>
        </DrawerContent>
      </Drawer>
      <Modal
        isOpen={Boolean(preview)}
        onClose={() => setPreview("")}
        size="3xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Scene illustration</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <img
              src={preview}
              alt="Expanded story scene"
              style={{ width: "100%", borderRadius: 12 }}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={Boolean(edit)}
        onClose={() => !chat.generating && setEdit(null)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Edit {edit?.role === "user" ? "your message" : "reply"}
          </ModalHeader>
          <ModalCloseButton isDisabled={chat.generating} />
          <ModalBody>
            <Textarea
              aria-label="Edit message text"
              rows={6}
              value={editText}
              maxLength={4000}
              onChange={(e) => setEditText(e.target.value)}
            />
            {edit?.role === "user" && (
              <p className="field-hint">
                Saving generates a replacement for the latest reply. The current
                reply stays safe if generation fails.
              </p>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button
              variant="ghost"
              leftIcon={<FiX />}
              isDisabled={chat.generating}
              onClick={() => setEdit(null)}
            >
              Cancel
            </Button>
            <Button
              variant="brand"
              isLoading={chat.generating}
              isDisabled={!editText.trim() || chat.unavailable}
              onClick={saveEdit}
            >
              Save{edit?.role === "user" ? " and regenerate" : ""}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
